import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { InFeedAd } from '../components/AdSense';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Search,
  Play,
  FileText,
  Calendar,
  Clock,
  Eye,
  Heart,
  MessageSquare,
  Share2,
  Bookmark,
  ChevronRight,
  TrendingUp,
  Sparkles,
  Video,
  Newspaper,
  Filter,
  Rss
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Blog/Vlog Page - News and information hub
 */
const Blog = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');

  const categories = [
    { id: 'all', label: 'All Posts', icon: Newspaper },
    { id: 'news', label: 'News', icon: TrendingUp },
    { id: 'tips', label: 'Pet Care Tips', icon: Heart },
    { id: 'health', label: 'Health & Wellness', icon: Sparkles },
    { id: 'training', label: 'Training', icon: FileText },
    { id: 'community', label: 'Community Stories', icon: MessageSquare },
    { id: 'video', label: 'Videos', icon: Video },
  ];

  useEffect(() => {
    fetchPosts();
  }, [activeCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      // Fetch blog posts from the blog API
      const categoryParam = activeCategory !== 'all' ? `&category=${activeCategory}` : '';
      const response = await fetch(`${API}/api/blog/posts?limit=20${categoryParam}`);
      
      if (response.ok) {
        const data = await response.json();
        // Set first featured post or first post as featured
        const featuredIdx = data.findIndex(p => p.is_featured);
        if (featuredIdx >= 0) {
          setFeaturedPost(data[featuredIdx]);
          setPosts(data.filter((_, i) => i !== featuredIdx));
        } else if (data.length > 0) {
          setFeaturedPost(data[0]);
          setPosts(data.slice(1));
        } else {
          setFeaturedPost(null);
          setPosts([]);
        }
      } else {
        setFeaturedPost(null);
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setFeaturedPost(null);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSearchParams(category !== 'all' ? { category } : {});
  };

  const filteredPosts = searchQuery
    ? posts.filter(post => 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : posts;

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <SEO 
        title="Blog"
        description="Dog care tips, health advice, training guides, and community news from All American Dog Club."
        url="/blog"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-medium mb-2">
                AADC Blog
              </h1>
              <p className="text-white/80">
                News, tips, and stories from our pet-loving community
              </p>
              <Link to="/feeds" className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white mt-1">
                <Rss className="w-3 h-3" /> View our news sources
              </Link>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white text-foreground"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={activeCategory === category.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => handleCategoryChange(category.id)}
                  className="flex-shrink-0"
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {category.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="space-y-6">
            <Skeleton className="h-80 w-full rounded-xl" />
            <div className="grid md:grid-cols-3 gap-6">
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
              <Skeleton className="h-64" />
            </div>
          </div>
        ) : (
          <>
            {/* Featured Post */}
            {featuredPost && (
              <Card className="mb-8 overflow-hidden" data-testid="featured-post">
                <div className={`grid ${featuredPost.image_url && featuredPost.image_url.trim() ? 'md:grid-cols-2' : ''}`}>
                  {featuredPost.image_url && featuredPost.image_url.trim() ? (
                    <div className="relative h-64 md:h-auto md:min-h-[280px]">
                      <img
                        src={featuredPost.image_url}
                        alt={featuredPost.image_alt || featuredPost.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                      {featuredPost.is_video && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-8 h-8 text-primary ml-1" />
                          </div>
                        </div>
                      )}
                      <Badge className="absolute top-4 left-4 bg-primary">Featured</Badge>
                    </div>
                  ) : (
                    <Badge className="absolute top-4 left-4 bg-primary z-10">Featured</Badge>
                  )}
                  <CardContent className="p-6 flex flex-col justify-center">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline">{featuredPost.category}</Badge>
                      {featuredPost.is_video && (
                        <Badge variant="secondary">
                          <Video className="w-3 h-3 mr-1" />
                          Video
                        </Badge>
                      )}
                    </div>
                    <h2 className="text-2xl font-heading font-medium mb-3">
                      {featuredPost.title}
                    </h2>
                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {featuredPost.excerpt || featuredPost.content?.substring(0, 200)}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {featuredPost.views_count?.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" />
                        {featuredPost.likes_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {featuredPost.read_time} min read
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={featuredPost.author_avatar} />
                          <AvatarFallback>{featuredPost.author_username?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <span className="text-sm">{featuredPost.author_username}</span>
                          {featuredPost.is_curated && featuredPost.external_source && (
                            <span className="block text-[10px] text-muted-foreground">via {featuredPost.external_source}</span>
                          )}
                        </div>
                      </div>
                      {featuredPost.is_curated && featuredPost.external_url ? (
                        <a href={featuredPost.external_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                          <Button>
                            Read Full Article
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </a>
                      ) : (
                        <Button onClick={() => navigate(`/blog/${featuredPost.id}`)}>
                          Read More
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </div>
              </Card>
            )}

            {/* Post Grid */}
            {filteredPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post) => (
                  <Card 
                    key={post.id} 
                    className="overflow-hidden card-interactive cursor-pointer"
                    onClick={() => {
                      if (post.is_curated && post.external_url) {
                        window.open(post.external_url, '_blank', 'noopener,noreferrer');
                      } else {
                        navigate(`/blog/${post.id}`);
                      }
                    }}
                    data-testid={`blog-post-${post.id}`}
                  >
                    <div className="relative h-48">
                      {post.image_url ? (
                        <img
                          src={post.image_url}
                          alt={post.image_alt || post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
                          <FileText className="w-12 h-12 text-muted-foreground/30" />
                        </div>
                      )}
                      {post.is_video && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-6 h-6 text-primary ml-0.5" />
                          </div>
                        </div>
                      )}
                      <Badge className="absolute top-3 left-3" variant="secondary">
                        {post.category}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium mb-2 line-clamp-2">{post.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div>
                          <span>{post.author_username}</span>
                          {post.is_curated && post.external_source && (
                            <span className="block text-[10px] opacity-70">via {post.external_source}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="w-3 h-3" />
                            {post.likes_count}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="text-center py-16">
                <CardContent>
                  <Newspaper className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">No posts found</h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery 
                      ? `No posts matching "${searchQuery}"`
                      : 'Check back soon for new content!'
                    }
                  </p>
                  {searchQuery && (
                    <Button variant="outline" onClick={() => setSearchQuery('')}>
                      Clear Search
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Newsletter Signup */}
            <Card className="mt-12 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <Sparkles className="w-10 h-10 mx-auto text-primary mb-4" />
                <h3 className="text-xl font-heading font-medium mb-2">
                  Stay Updated
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Get the latest pet care tips, community news, and exclusive content delivered to your inbox.
                </p>
                <div className="flex gap-2 max-w-md mx-auto">
                  <Input 
                    placeholder="Enter your email"
                    className="flex-1"
                  />
                  <Button className="btn-primary">Subscribe</Button>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  We respect your privacy. Unsubscribe anytime.
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Blog;
